/**
 * 1.3.0 the "Assign for delivery…" dialog (P2): a company or one of its users, a delivery spec, a due date in a
 * chosen time zone, an internal contact, notes to the supplier, and the requested items (the media item always,
 * the spec's default extra items copied in and editable, more added). The same dialog edits an open assignment.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Send, Trash2, Truck } from 'lucide-react';
import { ITEM_KIND_PRESET, ITEM_KINDS, ITEM_TYPE_PRESETS, type DeliveryItemTemplate, type ItemKind } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useAssignmentChoices, useCreateAssignments, useUpdateAssignment, type AssignmentDTO } from '../../api/hooks/assignments';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { browserZone, isoToZoned, timeZones, zonedToIso } from './zoned';

type ExtraKind = Exclude<ItemKind, 'media'>;
const EXTRA_KINDS = ITEM_KINDS.filter((k): k is ExtraKind => k !== 'media');
const KIND_LABEL: Record<ExtraKind, string> = { document: 'Document', subtitles: 'Subtitles', image: 'Image', audio: 'Audio', other: 'Other' };

interface ItemDraft { key: string; id?: string; kind: ExtraKind; label: string; description: string; types: string; required: boolean; locked: boolean }
let seq = 0;
const draftOf = (i: DeliveryItemTemplate & { id?: string }, locked = false): ItemDraft => ({
  key: `i${++seq}`, id: i.id, kind: i.kind, label: i.label, description: i.description ?? '', types: (i.acceptedTypes ?? []).join(', '), required: i.required ?? true, locked,
});
const typesOf = (s: string) => [...new Set(s.split(/[\s,;]+/).map((x) => x.trim().replace(/^\./, '').toLowerCase()).filter(Boolean))];
const typesText = (t: readonly string[]) => (t.length ? t.map((x) => `.${x}`).join(' ') : 'any type');

export interface AssignTarget { id: string; assetCode: string; title: string }

export function AssignDialog({ targets = [], skipped = 0, existing, onClose }: {
  targets?: AssignTarget[]; skipped?: number; existing?: AssignmentDTO; onClose: () => void;
}) {
  const choices = useAssignmentChoices().data;
  const create = useCreateAssignments();
  const update = useUpdateAssignment();
  const editing = Boolean(existing);

  const [who, setWho] = useState(existing ? (existing.portalUser ? `user:${existing.company.id}:${existing.portalUser.id}` : `company:${existing.company.id}`) : '');
  const [specId, setSpecId] = useState(existing?.spec?.id ?? '');
  const [zone, setZone] = useState(existing?.dueTimeZone || browserZone());
  const [due, setDue] = useState(isoToZoned(existing?.dueAt, existing?.dueTimeZone || browserZone()));
  const [contact, setContact] = useState(existing?.contact?.id ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [items, setItems] = useState<ItemDraft[]>(() => (existing?.items ?? []).filter((i) => i.kind !== 'media').map((i) => draftOf(
    { id: i.id, kind: i.kind as ExtraKind, label: i.label, description: i.description, acceptedTypes: i.acceptedTypes, required: i.required },
    i.state !== 'open' || Boolean(i.file) || Boolean(i.submissionId),
  )));
  const [touchedItems, setTouchedItems] = useState(editing);

  useEffect(() => { if (choices && !contact) setContact(choices.me); }, [choices, contact]);
  const spec = choices?.specs.find((s) => s.id === specId) ?? null;
  // A new assignment starts with the spec's default items, until the items are edited by hand.
  useEffect(() => {
    if (!touchedItems) setItems((spec?.defaultItems ?? []).map((i) => draftOf(i)));
  }, [spec, touchedItems]);

  const companyId = who.split(':')[1] ?? '';
  const portalUserId = who.startsWith('user:') ? who.split(':')[2]! : null;
  const company = choices?.companies.find((c) => c.id === companyId);
  const zones = useMemo(() => timeZones(zone), [zone]);
  const edit = (key: string, patch: Partial<ItemDraft>) => { setTouchedItems(true); setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x))); };
  const remove = (key: string) => { setTouchedItems(true); setItems((xs) => xs.filter((x) => x.key !== key)); };
  const add = () => { setTouchedItems(true); setItems((xs) => [...xs, draftOf({ kind: 'document', label: '', description: '', acceptedTypes: [...ITEM_TYPE_PRESETS.document], required: true })]); };

  const badItem = items.some((i) => !i.label.trim());
  const busy = create.isPending || update.isPending;
  const canSave = Boolean(companyId) && !badItem && !busy && (editing || targets.length > 0);

  async function save() {
    const body = {
      portalUserId, specId: specId || null, dueAt: due ? zonedToIso(due, zone) : null, timeZone: zone, notes,
      contactUserId: contact || null,
      items: items.map((i) => ({ ...(i.id ? { id: i.id } : {}), kind: i.kind, label: i.label.trim(), description: i.description, acceptedTypes: typesOf(i.types), required: i.required })),
    };
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, body: { ...body, contactUserId: body.contactUserId ?? undefined } });
        toast('Delivery updated; the supplier is told');
      } else {
        const r = await create.mutateAsync({ ...body, assetIds: targets.map((t) => t.id), companyId });
        const name = portalUserId ? company?.users.find((u) => u.id === portalUserId)?.name : company?.name;
        if (r.created.length) toast(`${r.created.length === 1 ? r.created[0]!.asset.assetCode : `${r.created.length} placeholders`} assigned to ${name ?? 'the supplier'}`);
        if (r.refused.length) {
          const code = (id: string) => targets.find((t) => t.id === id)?.assetCode ?? id;
          toast(`Not assigned: ${r.refused.map((x) => `${code(x.assetId)} (${x.message})`).join('; ')}`, 'error');
        }
      }
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const title = existing ? `Edit delivery of ${existing.asset.assetCode}` : targets.length === 1 ? `Assign ${targets[0]!.assetCode} for delivery` : `Assign ${targets.length} placeholders for delivery`;
  return (
    <Modal title={title} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!canSave} onClick={() => void save()} data-testid="assign-delivery-save">
        {busy ? <Loader2 size={14} className="spin" /> : existing ? <Send size={14} /> : <Truck size={14} />} {existing ? 'Save changes' : 'Assign'}
      </button>
    </>}>
      <div className="assign-dialog" data-testid="assign-delivery-dialog">
        {!existing && targets.length > 1 && (
          <p className="muted assign-targets">
            {targets.slice(0, 6).map((t) => t.assetCode).join(', ')}{targets.length > 6 ? ` and ${targets.length - 6} more` : ''}
            {skipped > 0 && <span className="faint"> · {skipped} selected asset{skipped === 1 ? '' : 's'} with media left out</span>}
          </p>
        )}
        {!existing && targets.length === 1 && <p className="muted assign-targets">{targets[0]!.title}</p>}

        <div className="assign-grid">
          <div className="field">
            <label htmlFor="assign-who">Deliver by <span className="req">*</span></label>
            <select id="assign-who" className="select" value={who} onChange={(e) => setWho(e.target.value)} data-testid="assign-who">
              <option value="">Choose a company or user…</option>
              {(choices?.companies ?? []).filter((c) => !existing || c.id === existing.company.id).map((c) => (
                <optgroup key={c.id} label={c.name}>
                  <option value={`company:${c.id}`}>{c.name}: every user</option>
                  {c.users.map((u) => <option key={u.id} value={`user:${c.id}:${u.id}`}>{u.name} ({u.email}){u.invited ? ', invited' : ''}</option>)}
                </optgroup>
              ))}
            </select>
            {choices && !choices.companies.length && <span className="help">No companies yet. An administrator adds them under Administration → Media portal.</span>}
            {company && !portalUserId && <span className="help">Every user of {company.name} sees it and is emailed.</span>}
          </div>
          <div className="field">
            <label htmlFor="assign-spec">Delivery spec</label>
            <select id="assign-spec" className="select" value={specId} onChange={(e) => setSpecId(e.target.value)} data-testid="assign-spec">
              <option value="">No spec (any media)</option>
              {(choices?.specs ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {spec?.description && <span className="help">{spec.description}</span>}
          </div>
          <div className="field">
            <label htmlFor="assign-due">Due</label>
            <div className="assign-due">
              <input id="assign-due" className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="assign-due" />
              <select className="select" aria-label="Time zone" value={zone} onChange={(e) => {
                const iso = due ? zonedToIso(due, zone) : null;
                setZone(e.target.value);
                if (iso) setDue(isoToZoned(iso, e.target.value));
              }} data-testid="assign-zone">
                {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <span className="help">The supplier's email shows the due date in this time zone.</span>
          </div>
          <div className="field">
            <label htmlFor="assign-contact">Internal contact</label>
            <select id="assign-contact" className="select" value={contact} onChange={(e) => setContact(e.target.value)} data-testid="assign-contact">
              {(choices?.contacts ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.id === choices?.me ? ' (you)' : ''}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="assign-notes">Notes to the supplier</label>
          <textarea id="assign-notes" className="input" rows={3} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the production company should know: versions, audio layout, deadlines for parts" data-testid="assign-notes" />
        </div>

        <div className="assign-items" data-testid="assign-items">
          <div className="assign-items-head">
            <h3>Requested items</h3>
            <button type="button" className="btn ghost sm" onClick={add} disabled={items.length >= 30} data-testid="assign-add-item"><Plus size={13} /> Add item</button>
          </div>
          <div className="assign-item media">
            <div className="assign-item-title"><strong>Media</strong><span className="pill accent">Always requested</span></div>
            <span className="faint">{spec ? `${spec.name}: ${typesText(spec.acceptedTypes)}` : 'Any media file PRISM can ingest'}</span>
          </div>
          {items.map((i, n) => (
            <div className="assign-item" key={i.key} data-testid="assign-item">
              <div className="assign-item-row">
                <select className="select" aria-label="Kind" value={i.kind} disabled={i.locked} data-testid="assign-item-kind"
                  onChange={(e) => { const kind = e.target.value as ExtraKind; edit(i.key, { kind, types: ITEM_TYPE_PRESETS[ITEM_KIND_PRESET[kind]].join(', ') }); }}>
                  {EXTRA_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
                <input className="input" aria-label="Label" placeholder="Label, e.g. Final manuscript" value={i.label} maxLength={200}
                  onChange={(e) => edit(i.key, { label: e.target.value })} data-testid="assign-item-label" aria-invalid={!i.label.trim()} />
                <label className="assign-required"><input type="checkbox" checked={i.required} onChange={(e) => edit(i.key, { required: e.target.checked })} data-testid="assign-item-required" /> Required</label>
                <button type="button" className="btn ghost icon sm" aria-label={`Remove item ${n + 1}`} disabled={i.locked}
                  title={i.locked ? 'Already uploaded: it can no longer be removed' : 'Remove'} onClick={() => remove(i.key)} data-testid="assign-item-remove"><Trash2 size={13} /></button>
              </div>
              <div className="assign-item-row">
                <input className="input" aria-label="Short description" placeholder="Short description, e.g. PDF, signed off by the director" value={i.description} maxLength={2000}
                  onChange={(e) => edit(i.key, { description: e.target.value })} data-testid="assign-item-description" />
                <input className="input assign-types" aria-label="Accepted types" placeholder="Any type" value={i.types} disabled={i.locked}
                  onChange={(e) => edit(i.key, { types: e.target.value })} title="File extensions, separated by commas; empty: any type" data-testid="assign-item-types" />
              </div>
              {i.locked && <span className="faint assign-locked">Uploaded by the supplier: kind and types are fixed.</span>}
            </div>
          ))}
          {!items.length && <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>Only the media. Add items for manuscripts, flyers, subtitles and so on.</p>}
        </div>
      </div>
    </Modal>
  );
}
